import { Theme, ColorTypes, useIsTheme, useIsDesignLanguage, DesignLanguage } from '@afterpaytouch/core'
import { CONVERGENCE_NAMESPACE, NAMESPACE_SEPARATOR, THEME_COOKIE_NAME, UseCustomTranslationResponse, SetThemeRequest, Features } from '../model'
import { useTranslation as useNextTranslation, UserConfig, SSRConfig } from 'next-i18next'
import { serverSideTranslations as originalServerSideTranslations } from 'next-i18next/serverSideTranslations'
import { setCookie } from 'nookies'
import { TOptions } from 'i18next'
import { isUndefinedOrEmpty } from '@afterpay/utils'
import { useCachedFeatureFlag, useCheckoutState, useCheckoutTheme, useCohort } from '../state'
import { Cohort } from '@afterpaytouch/portal-api'
import { useEffect, useMemo } from 'react'
import { useMerchantCallback } from './merchant'

export const generateI18nNamespace = (input: string[]): string[] => {
  return input.flatMap((item) => [item, `${item}${CONVERGENCE_NAMESPACE}`])
}

/**
 * 'originalKey' is the i18n key for original Afterpay copies.
 * 'primaryKey' is the i18n key for convergence copies based on cohort, can be either AP or Cash.
 * @param key
 * @param theme
 * @param cohort
 * @param options
 * @returns
 */
export const getTranslationKeys = (key: string | string[], cohort: Cohort | undefined = Cohort.APOnly, options?: TOptions): string | string[] => {
  const processSingleKey = (originalKey: string): string | string[] => {
    if (cohort !== Cohort.CashOnly) {
      return originalKey
    }

    const keys = originalKey.split(NAMESPACE_SEPARATOR)
    if (keys.length !== 2) {
      return originalKey
    }

    const [translationNamespace, translationKey] = keys

    const primaryKey = `${translationNamespace}${CONVERGENCE_NAMESPACE}:${translationKey}`

    // There is a weird bug where if there are two keys and returnObjects is true, for eg
    // ['terminalError__cash:amountTooHigh.option', 'terminalError:amountTooHigh.option']
    // It won't use the __cash version - but the fallback one.
    if (options?.returnObjects) {
      return [primaryKey]
    }

    return [primaryKey, originalKey]
  }

  if (Array.isArray(key)) {
    const processedKeys = key.reduce<Array<string | string[]>>((acc, cur) => {
      const processed = processSingleKey(cur)
      return Array.isArray(processed) ? [...acc, ...processed] : [...acc, processed]
    }, [])
    return processedKeys.flat()
  } else {
    return processSingleKey(key)
  }
}

// Note: this custom useTranslation hook generate additional translation key for convergence
// If you need to use diff copy for Cohort.CashOnly, for eg: summary__cash.json, please use this hook

export const useTranslation = (ns?: string[]): UseCustomTranslationResponse => {
  const cohort = useCohort()
  const { t: originalTFunction, i18n, ready } = useNextTranslation(ns)

  const enrichedT = (originalKey: string | string[], options?: TOptions): string => {
    const convergenceKeys = getTranslationKeys(originalKey, cohort, options)
    return originalTFunction(convergenceKeys, options)
  }

  return useMemo(
    () => ({
      t: enrichedT,
      i18n,
      ready,
    }),
    [enrichedT, i18n, ready]
  )
}

export const serverSideTranslations = async (
  initialLocale: string,
  namespacesRequired: string[],
  configOverride?: UserConfig | null,
  extraLocales?: string[] | false
): Promise<SSRConfig> => {
  const convergenceNameSpaces = generateI18nNamespace(namespacesRequired)
  return await originalServerSideTranslations(initialLocale, convergenceNameSpaces, configOverride, extraLocales)
}

export const updateTheme = async (data: SetThemeRequest): Promise<void> => {
  try {
    setCookie(null, THEME_COOKIE_NAME, data.theme, {
      // maxAge: 30 * 24 * 60 * 60, // 30 days
      // maxAge: 6 * 60 * 60, // 6 hours
      path: '/',
      // httpOnly: true, // cater for payment schedule widget
      secure: true,
      sameSite: 'none',
      // domain // cater for payment schedule widget
    })
  } catch (error) {
    console.error('Failed to set theme')
  }
}

export const isValidThemeValue = (value?: string): value is Theme => !isUndefinedOrEmpty(value) && Object.values(Theme).includes(value as Theme)

export const useSubHeadingColor = (): ColorTypes => {
  const isAPTheme = useIsTheme(Theme.AP)

  return isAPTheme ? 'Black' : 'Gray40'
}

export const isValidCohortValue = (value?: string): value is Cohort => !isUndefinedOrEmpty(value) && Object.values(Cohort).includes(value as Cohort)

export const useIsRedesignedSummaryPage = (): boolean => {
  const isArcadeDesignTheme = useIsDesignLanguage(DesignLanguage.Arcade)
  const isRedesignedSummaryPageEnabled = useCachedFeatureFlag(Features.REDESIGNED_SUMMARY_PAGE_ENABLED)

  return isArcadeDesignTheme && isRedesignedSummaryPageEnabled
}

export const useUpdateAfterpayJsBrand = (): void => {
  const [theme] = useCheckoutTheme()
  const { initial } = useCheckoutState()
  const { updateBrand } = useMerchantCallback()

  useEffect(() => {
    if (initial.isWindowed) {
      updateBrand(theme)?.catch(() => {})
    }
  }, [theme])
}

/**
 *  Determines if a consumer uuid is a shadow account or not.
 *  @param {string} consumerUuid
 *  @returns {boolean} Returns `true` if is a shadow account.
 */

export const isShadowAccount = (consumerUuid: string): boolean => {
  return consumerUuid.startsWith('C_')
}
